import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import AdminCreatePaymentModal from './AdminCreatePaymentModal';

// Destructure all used icons from FiIcons
const { 
  FiSearch, 
  FiUser, 
  FiZap, 
  FiShield, 
  FiMoreHorizontal, 
  FiDollarSign, 
  FiTrash2, 
  FiExternalLink, 
  FiCheckCircle 
} = FiIcons;

export default function UserManagement({ onViewStatement, onRefresh }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUserForPayment, setSelectedUserForPayment] = useState(null);
  const [generatedUrl, setGeneratedUrl] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (!error) setUsers(data);
    setLoading(false);
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(search.toLowerCase()) || 
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar nome ou email..."
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full py-20 text-center text-slate-400">Carregando usuários...</div>
        ) : filteredUsers.map(u => (
          <motion.div 
            layout key={u.id}
            className="bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm hover:border-indigo-100 transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-500 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <SafeIcon icon={FiUser} className="w-6 h-6" />
              </div>
              <div className="flex flex-col items-end">
                <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider mb-1 ${
                  u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                }`}>
                  {u.role}
                </span>
                <span className={`text-[10px] font-bold ${u.status === 'active' ? 'text-emerald-500' : 'text-red-500'}`}>
                  ● {u.status}
                </span>
              </div>
            </div>

            <div className="mb-6">
              <p className="font-black text-slate-900 truncate tracking-tight">{u.full_name || 'Sem nome'}</p>
              <p className="text-xs text-slate-400 truncate">{u.email}</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl mb-6">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiZap} className="text-indigo-600" />
                <span className="text-xs font-bold text-slate-600">Saldo</span>
              </div>
              <span className="font-black text-slate-900">{u.credits}</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => onViewStatement(u)}
                className="py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-colors"
              >
                Histórico
              </button>
              <button 
                onClick={() => setSelectedUserForPayment(u)}
                className="py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiDollarSign} />
                Cobrar
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedUserForPayment && (
          <AdminCreatePaymentModal 
            user={selectedUserForPayment}
            onClose={() => setSelectedUserForPayment(null)}
            onSuccess={(url) => {
              setSelectedUserForPayment(null);
              setGeneratedUrl(url);
            }}
          />
        )}

        {generatedUrl && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setGeneratedUrl(null)} />
            <div className="relative w-full max-w-sm bg-white rounded-[2.5rem] p-8 text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <SafeIcon icon={FiCheckCircle} className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-black text-slate-900">Cobrança Gerada!</h3>
              <p className="text-sm text-slate-500">O link de pagamento foi criado com sucesso. Você pode enviá-lo ao usuário.</p>
              <a 
                href={generatedUrl} target="_blank" rel="noreferrer"
                className="block w-full py-4 bg-indigo-600 text-white rounded-2xl font-black flex items-center justify-center gap-2"
              >
                Abrir Link <SafeIcon icon={FiExternalLink} />
              </a>
              <button 
                onClick={() => setGeneratedUrl(null)}
                className="text-slate-400 font-bold text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}